import { useEffect, useState, useMemo } from "react";
import { EmptyPlaceholder } from "../common";
import classnames from "classnames";
import { SelectionPanelSearch } from '../amrPipeline/common/SelectionPanelSearch';
import { CloManagersNavItem } from './CloManagersNavItem';
import { PipelineTabs, Tab } from '../amrPipeline/aggregated/PipelineTabs';
import React from "react";
import { useSelector } from "react-redux";
import { AppState } from "../../types/state/AppState";
import { UserConfigState } from "../../types/state/UserConfigState";
import { AlertOption } from "../../types/email-preferences/EmailPreferences";
import { getAlertValues, isCollateralManagersOwnCompany } from "../../utils/amr-pipeline.utils";
import { user } from "../../user/user";
import { SubscriptionFeature } from "../../types/billing/SubscriptionFeature";
import { CloManager } from "../../types/state/CloManagersState";
import { CloManagerStatus } from "../../types/clo-managers/CloManagerDetailed";

enum SearchTab {
    Active = 'Active',
    Favourite = 'Favorite',
    Inactive = 'Inactive',
}

interface Props {
    data: CloManager[];
    selectedReferenceName: string;
    onChange: (referenceName: string) => void;
    onFavoriteClick: (referenceName: string, favorite: boolean) => void;
}

const searchManagers = (cloManagers: CloManager[], searchTerm: string, cloManagerStatus?: CloManagerStatus) => {
    return cloManagers.filter(({ legalName, status }) =>
        legalName
            .toLocaleLowerCase()
            .includes(searchTerm.toLocaleLowerCase().trim()) && (
                cloManagerStatus == null || status === cloManagerStatus
            )
    );
};

const getFavorites = (cloManagers: CloManager[]) => {
    return cloManagers.filter(({ favorite }) => favorite);
}

export const CloManagersSelectionPanel = ({
    data,
    selectedReferenceName,
    onChange,
    onFavoriteClick,
}: Props) => {
    const [collapsed, setCollapsed] = useState(false);
    const [tab, setTab] = useState(SearchTab.Active);
    const [tabSearchTerm, setTabSearchTerm] = useState({
        [SearchTab.Active]: '',
        [SearchTab.Favourite]: '',
        [SearchTab.Inactive]: '',
    });

    const userCompany = useSelector((state: AppState) => state.issuanceMonitor.amrPipelineCommon.userCompany);

    const alertConfigs = useSelector<AppState, UserConfigState>((state: AppState) => state.imUserConfig);

    const cloManagerAnalyticsConfig = getAlertValues(alertConfigs.cloManagerAnalyticsAlert.value);
    const cloManagerImConfig = getAlertValues(alertConfigs.cloManagerIssuanceMonitorAlert.value);
    const cloManagerApConfig = getAlertValues(alertConfigs.cloManagerArrangerPipelineAlert.value);
    const cloManagerBwicConfig = getAlertValues(alertConfigs.cloManagerBwicAlert.value);
    const cloManagerDealerInventoryConfig = getAlertValues(alertConfigs.cloManagerDealerInventoryAlert.value);
    const cloManagerOutOfRiConfig = getAlertValues(alertConfigs.cloManagerOutOfRiAlert.value);
    const cloManagerOutOfNcConfig = getAlertValues(alertConfigs.cloManagerOutOfNcAlert.value);

    const hasAlertAccess = user.hasAllFeatures(
        SubscriptionFeature.ManagerProfileManagerRelatedAlerts,
        SubscriptionFeature.IssuanceMonitorFiltersEmailAlerts,
    );


    const selectedManagerRef = React.useRef<HTMLDivElement>(null);

    useEffect(() => {
        var customEvent = document.createEvent("Events");
        customEvent.initEvent("resize", true, true);
        window.dispatchEvent(customEvent);
    }, [collapsed]);

    useEffect(() => {
        if (selectedReferenceName) {
            selectedManagerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const handleSearch = (search: string) => {
        setTabSearchTerm(prevValue => ({
            ...prevValue,
            [tab]: search,
        }));
    };

    const getManagers = () => {
        const searchTerm = tabSearchTerm[tab];

        if(tab === SearchTab.Favourite) return searchManagers(getFavorites(data), searchTerm);
        if(tab === SearchTab.Active) return searchManagers(data, searchTerm, CloManagerStatus.Active);
        if(tab === SearchTab.Inactive) return searchManagers(data, searchTerm, CloManagerStatus.Inactive);
    }

    const getEmptyText = () => {
        const searchTerm = tabSearchTerm[tab];

        if (searchTerm || tab === SearchTab.Active) {
            return undefined;
        } else if (tab === SearchTab.Favourite) {
            return 'There are no favorite managers yet.';
        }

        return 'There are no inactive managers yet.';
    };

    const favoritesNumber = useMemo(
        () => searchManagers(getFavorites(data), "").length,
        [data]);

    const cloManagers = getManagers();

    const isEmpty = !cloManagers?.length;

    return (
        <aside
            className={classnames("sidebar sidebar-nav sidebar-nav-clo-managers", { collapsed })}
            onClick={collapsed ? () => setCollapsed(false) : undefined}
        >
            <SelectionPanelSearch
                searchTerm={tabSearchTerm[tab]}
                collapsed={collapsed}
                placeholder="Search by manager"
                onCollapseClick={setCollapsed}
                onSearch={handleSearch}
            />
            <PipelineTabs value={tab} onChange={setTab} className="tabs-left-shift-sm">
                <Tab title={SearchTab.Active} key={SearchTab.Active} value={SearchTab.Active} />
                <Tab
                    key={SearchTab.Favourite}
                    title={
                        <>
                            <span>{SearchTab.Favourite}</span>
                            {!!favoritesNumber && <span className="text-warm-grey">{` (${favoritesNumber})`}</span>}
                        </>
                    }
                    value={SearchTab.Favourite}
                />
                <Tab title={SearchTab.Inactive} key={SearchTab.Inactive} value={SearchTab.Inactive} />
            </PipelineTabs>
            <nav className="sidebar-nav-list">
                {isEmpty && <EmptyPlaceholder textView text={getEmptyText()} />}
                {cloManagers?.map(cloManager => {
                    const { referenceName } = cloManager;

                    const isOwnCompany = isCollateralManagersOwnCompany(cloManager, userCompany);

                    const selectedCloManagerAnalyticsAlert = cloManagerAnalyticsConfig.get(referenceName)?.alertOption;
                    const imAlertOption = hasAlertAccess ? cloManagerImConfig.get(referenceName)?.alertOption : AlertOption.Never;
                    const apAlertOption = hasAlertAccess ? cloManagerApConfig.get(referenceName)?.alertOption : AlertOption.Never;
                    const bwicAlertOption = cloManagerBwicConfig.get(referenceName)?.alertOption;
                    const dealerInventoryAlertOption = cloManagerDealerInventoryConfig.get(referenceName)?.alertOption;
                    const outOfRiAlertOption = cloManagerOutOfRiConfig.get(referenceName)?.alertOption;
                    const outOfNcAlertOption = cloManagerOutOfNcConfig.get(referenceName)?.alertOption;

                    const withAlert = [
                        isOwnCompany ? selectedCloManagerAnalyticsAlert : AlertOption.Never,
                        imAlertOption,
                        apAlertOption,
                        bwicAlertOption,
                        dealerInventoryAlertOption,
                        outOfRiAlertOption,
                        outOfNcAlertOption,
                    ].some(alert => alert && alert !== AlertOption.Never);

                    return (
                        <CloManagersNavItem
                            ref={referenceName === selectedReferenceName ? selectedManagerRef : undefined}
                            key={referenceName}
                            searchTerm={tabSearchTerm[tab]}
                            selected={selectedReferenceName === referenceName}
                            cloManager={cloManager}
                            onClick={onChange}
                            withAlert={withAlert}
                            onFavoriteClick={onFavoriteClick}
                        />
                    );
                })}
            </nav>
        </aside>
    );
};
